import { TrendUp, TrendDown } from '@phosphor-icons/react'
import { cn } from '@/lib/utils'
import { BarChart, Bar, ResponsiveContainer } from 'recharts'
import { useReducedMotion } from 'framer-motion'
import type { KpiMetric } from '@/types/analytics'

interface KpiCardProps {
  label: string
  metric: KpiMetric
  sparkline?: number[]
  format?: 'number' | 'percent' | 'hours'
  invertColor?: boolean
  /** Sparkline accent color — restrict to project palette (indigo / amber / emerald / red / violet). */
  color?: string
  /** Plain-language explanation of how the number is computed, shown on hover and to assistive tech. */
  hint?: string
}

function formatValue(value: number | null, format: 'number' | 'percent' | 'hours'): string {
  if (value === null) return '—'
  switch (format) {
    case 'percent': return `${value.toFixed(1)}%`
    case 'hours':   return `${value.toFixed(1)}h`
    case 'number':
    default:        return value.toLocaleString()
  }
}

function formatDelta(metric: KpiMetric): string | null {
  if (metric.change_pct !== undefined && metric.change_pct !== null) {
    const sign = metric.change_pct > 0 ? '+' : ''
    return `${sign}${metric.change_pct.toFixed(1)}%`
  }
  if (metric.change_pts !== undefined && metric.change_pts !== null) {
    const sign = metric.change_pts > 0 ? '+' : ''
    return `${sign}${metric.change_pts.toFixed(1)}pp`
  }
  return null
}

function isPositiveChange(metric: KpiMetric): boolean {
  if (metric.change_pct !== undefined && metric.change_pct !== null) return metric.change_pct > 0
  if (metric.change_pts !== undefined && metric.change_pts !== null) return metric.change_pts > 0
  return false
}

export default function KpiCard({
  label,
  metric,
  sparkline,
  format = 'number',
  invertColor = false,
  color = '#5e6ad2',
  hint,
}: KpiCardProps) {
  // Recharts animates via react-smooth (JS-driven), so it ignores both the global
  // CSS reduced-motion guard and framer-motion's <MotionConfig>. Gate it explicitly.
  const prefersReduced = useReducedMotion()
  const delta = formatDelta(metric)
  const positive = isPositiveChange(metric)
  const isUp = positive
  const isGood = invertColor ? !positive : positive
  // Treat a literal zero delta as neutral — neither red nor green. Avoids the
  // visual chaos where a row of "0.0pp" pills paints red/red/red/red/green.
  const isZero =
    (metric.change_pct !== undefined && metric.change_pct !== null && metric.change_pct === 0) ||
    (metric.change_pts !== undefined && metric.change_pts !== null && metric.change_pts === 0)

  const sparkData = sparkline?.map((v) => ({ v }))
  const sparkAllZero = !sparkData || sparkData.length === 0 || sparkData.every((d) => !d.v)

  return (
    <div
      className={cn(
        'flex flex-col gap-2.5 rounded-xl border p-5 transition-colors duration-150',
        'bg-card border-border/50 hover:border-border/80',
        'dark:bg-white/[0.02] dark:border-white/[0.08] dark:hover:border-white/[0.14]',
      )}
    >
      <span
        className="text-muted-foreground dark:text-[#8a8f98] uppercase"
        style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.08em' }}
        title={hint}
      >
        {label}
      </span>

      <div className="flex items-end justify-between gap-2">
        <span
          className="text-foreground dark:text-[#f7f8f8] tabular-nums"
          style={{ fontSize: '28px', fontWeight: 590, letterSpacing: '-0.5px', lineHeight: 1.05 }}
        >
          {formatValue(metric.value, format)}
        </span>

        {delta !== null && (
          <div
            className={cn(
              'inline-flex items-center gap-1 px-2 py-0.5 rounded-full border tabular-nums',
              isZero
                ? 'bg-muted/60 text-muted-foreground border-border/50 dark:bg-white/[0.05] dark:text-[#8a8f98] dark:border-white/[0.08]'
                : isGood
                  ? 'bg-emerald-500/10 text-emerald-700 border-emerald-500/25 dark:bg-emerald-500/[0.12] dark:text-emerald-400 dark:border-emerald-500/25'
                  : 'bg-red-500/10 text-red-700 border-red-500/25 dark:bg-red-500/[0.12] dark:text-red-400 dark:border-red-500/25',
            )}
            style={{ fontSize: '11px', fontWeight: 590, letterSpacing: '-0.05px' }}
          >
            {isZero
              ? <span className="inline-block h-0.5 w-2 rounded-full bg-current opacity-70" aria-hidden="true" />
              : isUp
                ? <TrendUp className="h-3 w-3" weight="bold" />
                : <TrendDown className="h-3 w-3" weight="bold" />}
            <span>{delta}</span>
          </div>
        )}
      </div>

      {/* Sparkline space is always reserved so all cards in a row line up.
          When there's no movement yet, render a faint baseline instead of an
          empty (invisible) bar chart.

          Decorative, so `aria-hidden` rather than recharts' `accessibilityLayer`
          (§61): the value and the delta above it are already text, and a
          keyboard-focusable 7-bar chart per KPI would add 35 stops to the row
          that announce nothing new. */}
      <div className="h-10 mt-1" aria-hidden="true">
        {sparkData && sparkData.length > 0 && !sparkAllZero ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={sparkData}>
              <Bar dataKey="v" fill={color} radius={[3, 3, 0, 0]} opacity={0.75} isAnimationActive={!prefersReduced} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full w-full flex items-end">
            <div className="h-[2px] w-full rounded-full bg-[#5e6ad2]/15 dark:bg-[#7170ff]/20" />
          </div>
        )}
      </div>
    </div>
  )
}
